import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client';

interface LogEntry {
  id: number;
  value: number;
  note: string | null;
  logged_at: string;
}

interface Goal {
  id: number;
  title: string;
  goal_type: 'checkbox' | 'numeric';
  unit: string | null;
}

export default function ProgressDetail() {
  const navigate = useNavigate();
  const { goalId } = useParams();
  const [goal, setGoal] = useState<Goal | null>(null);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!goalId) return;
    setLoading(true);
    api.getProgress(goalId)
      .then((res: any) => {
        setGoal(res.goal);
        setLogs(res.logs || []);
      })
      .catch(() => alert('Failed to load progress'))
      .finally(() => setLoading(false));
  }, [goalId]);

  const handleDelete = async (logId: number) => {
    if (!confirm('Delete this log?')) return;
    try {
      await api.deleteProgress(String(logId));
      setLogs((prev) => prev.filter((l) => l.id !== logId));
    } catch {
      alert('Failed to delete log');
    }
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="px-4 pt-6 pb-4">
      <div className="flex items-center mb-6">
        <button onClick={() => navigate(-1)} className="text-slate-400 hover:text-white mr-3 text-lg">←</button>
        <div>
          <h1 className="text-xl font-bold">{goal?.title}</h1>
          <p className="text-xs text-slate-400">{logs.length} entries</p>
        </div>
      </div>

      {logs.length === 0 ? (
        <div className="bg-slate-800 rounded-2xl p-8 text-center border border-slate-700/50">
          <div className="text-4xl mb-3">📋</div>
          <p className="text-sm text-slate-400">No progress logged yet. Start tracking!</p>
        </div>
      ) : (
        <div className="space-y-2">
          {logs.map((log) => (
            <div
              key={log.id}
              className="bg-slate-800 rounded-xl p-4 border border-slate-700/50 flex items-center justify-between"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="font-semibold text-lg">
                    {log.value}
                    {goal?.goal_type === 'numeric' && goal.unit && (
                      <span className="text-sm font-normal text-slate-400 ml-1">{goal.unit}</span>
                    )}
                    {goal?.goal_type === 'checkbox' && (
                      <span className="text-sm font-normal text-emerald-400 ml-1">done</span>
                    )}
                  </span>
                </div>
                {log.note && (
                  <p className="text-xs text-slate-400 truncate">{log.note}</p>
                )}
                <p className="text-[10px] text-slate-500 mt-1">
                  {formatDate(log.logged_at)} · {formatTime(log.logged_at)}
                </p>
              </div>
              <button
                onClick={() => handleDelete(log.id)}
                className="text-slate-500 hover:text-red-400 p-2 ml-2 transition-colors"
              >
                🗑️
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
